import { AlertType } from '@aloft/toolkit-shared';

import { useEffect } from 'react';

import {
  shouldPreserveAlertHoverPreview,
  syncAlertHoverPreviewFromType,
} from '../modules/alerts/syncAlertHoverPreview.ts';
import { useAlertsRef } from '../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import {
  ALERT_TOAST_DATA_ATTR,
  ALERT_TOAST_PROPERTY_ID_ATTR,
  COMPARABLES_ALERTS_TOAST_CLASS_NAME,
} from './useToaster/constants.ts';
import { useAppDispatch } from '../redux/hooks.ts';

export const getAlertTypeFromToastElement = (
  element: HTMLElement,
): { alertType: AlertType; propertyID?: string } | null => {
  const toastBar = element.closest(`.${COMPARABLES_ALERTS_TOAST_CLASS_NAME}`);
  if (!toastBar) {
    return null;
  }

  const toastContent = toastBar.querySelector(`[${ALERT_TOAST_DATA_ATTR}]`);
  const alertType = toastContent?.getAttribute(ALERT_TOAST_DATA_ATTR);
  if (!alertType) {
    return null;
  }

  const propertyID = toastContent?.getAttribute(ALERT_TOAST_PROPERTY_ID_ATTR) ?? undefined;

  return { alertType: alertType as AlertType, propertyID };
};

export const useAlertToastHoverSync = () => {
  const dispatch = useAppDispatch();
  const alertsRef = useAlertsRef();

  useEffect(() => {
    const toasterRoot = document.getElementById('_rht_toaster');
    if (!toasterRoot) {
      return undefined;
    }

    const setHoveredFromEvent = (event: MouseEvent) => {
      const alertFromToast = getAlertTypeFromToastElement(event.target as HTMLElement);
      syncAlertHoverPreviewFromType(
        dispatch,
        alertFromToast?.alertType ?? null,
        alertsRef.current,
        alertFromToast?.propertyID,
      );
    };

    const clearHoveredUnlessMovingToToast = (event: MouseEvent) => {
      const fromToast = (event.target as HTMLElement).closest(
        `.${COMPARABLES_ALERTS_TOAST_CLASS_NAME}`,
      );
      if (!fromToast) {
        return;
      }

      if (shouldPreserveAlertHoverPreview(event.relatedTarget as HTMLElement | null)) {
        return;
      }

      syncAlertHoverPreviewFromType(dispatch, null, alertsRef.current);
    };

    toasterRoot.addEventListener('mouseover', setHoveredFromEvent);
    toasterRoot.addEventListener('mouseout', clearHoveredUnlessMovingToToast);

    return () => {
      toasterRoot.removeEventListener('mouseover', setHoveredFromEvent);
      toasterRoot.removeEventListener('mouseout', clearHoveredUnlessMovingToToast);
    };
  }, [alertsRef, dispatch]);
};
